import { FC, useMemo } from "react";
import RecentSearchCard from "../components/RecentSearchCard";
import SortableFavoriteSearchGrid from "../components/SortableFavoriteSearchGrid";
import { Search } from "@/models/search/Search";
import useSearch from "@/hooks/useSearch";
import { FavoriteSearchesWidgetConfig } from "@/models/dashboard/WidgetConfig";
import { Skeleton } from "@capacit-com/lara-law-shared-ui";
import { Star } from "lucide-react";
import { dedupeSupersededTagSearches } from "../lib/dedupeSupersededTagSearches";
import {
  buildFullDisplayOrderForGlobalUpdate,
  resolveItemOrder,
} from "../lib/resolveItemOrder";

interface FavoriteSearchesWidgetProps {
  widgetId: string;
  name: string;
  config: FavoriteSearchesWidgetConfig;
  isEditMode: boolean;
  onConfigUpdate?: (config: FavoriteSearchesWidgetConfig) => void;
}

interface FavoriteSearchItem {
  id: string;
  search: Search;
}

const FavoriteSearchesWidget: FC<FavoriteSearchesWidgetProps> = ({
  widgetId: _widgetId,
  name: _name,
  config,
  isEditMode,
  onConfigUpdate,
}) => {
  const {
    useMyFavoriteSearches,
    favoriteSearch,
    unfavoriteSearch,
    deleteSearch,
    isAnyFavoritePending,
    isDeletePending,
    transformStoredSearch,
  } = useSearch();
  const fetchedFavoriteSearches = useMyFavoriteSearches();

  const displayOrder = useMemo(
    () => fetchedFavoriteSearches.data?.displayOrder ?? [],
    [fetchedFavoriteSearches.data?.displayOrder]
  );

  const favoriteSearches = useMemo(
    () =>
      dedupeSupersededTagSearches(
        fetchedFavoriteSearches.data?.searches?.map(transformStoredSearch) ?? []
      ),
    [fetchedFavoriteSearches.data?.searches, transformStoredSearch]
  );

  const layout = config.layout ?? "grid";

  const allFavoriteSearchItems = useMemo(() => {
    const items: FavoriteSearchItem[] = favoriteSearches.map(
      (search, index) => ({
        id:
          search.id ??
          search.favoriteLinkId ??
          `${search.searchTerm}-${search.searchedAt ?? "no-date"}-${index}`,
        search,
      })
    );

    return resolveItemOrder(items, {
      globalOrder: displayOrder,
      itemOrder: config.itemOrder,
      useGlobalOrder: config.useGlobalOrder,
    });
  }, [favoriteSearches, displayOrder, config.itemOrder, config.useGlobalOrder]);

  const favoriteSearchItems = useMemo(() => {
    if (config.maxItems && config.maxItems > 0) {
      return allFavoriteSearchItems.slice(0, config.maxItems);
    }

    return allFavoriteSearchItems;
  }, [allFavoriteSearchItems, config.maxItems]);

  const handleReorder = (visibleOrderedIds: string[]) => {
    if (!isEditMode || !onConfigUpdate) return;

    const allIds = allFavoriteSearchItems.map((item) => item.id);
    const itemOrder = buildFullDisplayOrderForGlobalUpdate(
      visibleOrderedIds,
      allIds,
      config.itemOrder ?? displayOrder
    );

    onConfigUpdate({
      ...config,
      useGlobalOrder: false,
      itemOrder,
    });
  };

  if (fetchedFavoriteSearches.isPending) {
    return (
      <div
        className={
          layout === "list"
            ? "space-y-2 p-2"
            : "grid grid-cols-2 gap-2 p-2"
        }
      >
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    );
  }

  if (favoriteSearchItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 space-y-3 text-muted-foreground">
        <Star className="h-8 w-8" />
        <p className="text-sm">Ingen favoriserede søgninger</p>
      </div>
    );
  }

  const gridClassName =
    layout === "list" ? "flex flex-col gap-2 p-2" : "grid grid-cols-2 gap-2 p-2";

  return (
    <div className="themed-scrollbar h-full overflow-auto">
      {isEditMode && (
        <p className="text-xs text-muted-foreground px-3 py-1 border-b border-border">
          Træk for at ændre rækkefølge — gem dashboard. Brug widget-indstillinger
          for global rækkefølge.
        </p>
      )}
      <SortableFavoriteSearchGrid
        items={favoriteSearchItems}
        layout={layout}
        disabled={!isEditMode}
        onReorder={handleReorder}
        className={gridClassName}
        renderItem={(item, dragHandleProps) => (
          <RecentSearchCard
            search={item.search}
            colors={[]}
            favoriteSearch={favoriteSearch.mutateAsync}
            unfavoriteSearch={unfavoriteSearch.mutateAsync}
            deleteSearch={deleteSearch.mutateAsync}
            isDeletingPending={isDeletePending}
            isAnyFavoritePending={isAnyFavoritePending}
            dragHandleProps={dragHandleProps}
          />
        )}
      />
    </div>
  );
};

export default FavoriteSearchesWidget;
